import cloneDeepWith from 'lodash-es/cloneDeepWith';
import mapValues from 'lodash-es/mapValues';
import omit from 'lodash-es/omit';
import omitBy from 'lodash-es/omitBy';

const FILTERED = '[Filtered]';
const WORD_EMAIL_REGEX = /[\p{L}\p{N}_.%+-]+(@[\p{L}\p{N}_-]+(\.[\p{L}\p{N}_-]+)*\.\p{L}{2,})?/gu;
const DOM_ATTRIBUTE_REGEX = /\[(aria-label|title|alt)="[\s\S]*?(?:"\](?=\[|\s|$)|$)/g;
const URL_KEYS = new Set(['url', 'from', 'to', 'http.url', 'lcp.url']);

function scrubEmails(value) {
  if (typeof value !== 'string' || !value.includes('@')) return value;
  return value.replace(WORD_EMAIL_REGEX, (word, email_domain) => (email_domain ? FILTERED : word));
}

function scrubText(value) {
  if (typeof value !== 'string') return value;
  return scrubEmails(value.replace(DOM_ATTRIBUTE_REGEX, `[$1="${FILTERED}"]`));
}

function scrubError(error) {
  return scrubTextDeep({ ...error, name: error.name, message: error.message, stack: error.stack });
}

function scrubItem(item) {
  if (typeof item === 'string') return scrubText(item);
  if (item instanceof Error) return scrubError(item);
  return undefined;
}

function scrubTextDeep(value) {
  return cloneDeepWith(value, scrubItem);
}

function stripQuery(value) {
  if (typeof value !== 'string') return value;
  return value.split('?')[0];
}

function isQueryKey(key) {
  return key === 'http.query' || key.startsWith('query.');
}

function scrubData(data) {
  if (!data) return data;
  return mapValues(
    omitBy(data, (_value, key) => isQueryKey(key)),
    (value, key) => (URL_KEYS.has(key) ? scrubText(stripQuery(value)) : scrubTextDeep(value)),
  );
}

function scrubRequest(request) {
  if (!request) return request;
  const request_scrubbed = scrubTextDeep(omit(request, 'query_string'));
  const headers = request_scrubbed.headers
    ? { ...request_scrubbed.headers, Referer: stripQuery(request_scrubbed.headers.Referer) }
    : request_scrubbed.headers;
  return { ...request_scrubbed, url: stripQuery(request_scrubbed.url), headers };
}

function scrubContexts(contexts) {
  const contexts_scrubbed = scrubTextDeep(contexts);
  if (!contexts_scrubbed || !contexts_scrubbed.trace) return contexts_scrubbed;
  return { ...contexts_scrubbed, trace: { ...contexts_scrubbed.trace, data: scrubData(contexts_scrubbed.trace.data) } };
}

function scrubSpanDescription(span) {
  const description = scrubText(span.description);
  return typeof span.op === 'string' && span.op.startsWith('resource.') ? stripQuery(description) : description;
}

function scrubSpans(spans) {
  if (!spans) return spans;
  return spans.map((span) => scrubSpan(span));
}

export function scrubSpan(span) {
  return { ...span, description: scrubSpanDescription(span), data: scrubData(span.data) };
}

export function scrubBreadcrumb(breadcrumb) {
  return { ...breadcrumb, message: scrubText(breadcrumb.message), data: scrubData(breadcrumb.data) };
}

export function scrubEvent(event) {
  return {
    ...event,
    message: scrubText(event.message),
    exception: scrubTextDeep(event.exception),
    extra: scrubTextDeep(event.extra),
    contexts: scrubContexts(event.contexts),
    request: scrubRequest(event.request),
    spans: scrubSpans(event.spans),
  };
}
